import { useParams, Link } from 'react-router-dom';
import { useEffect, useRef, useState, Children } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getPostBySlug, getAllPosts } from './posts/index.js';
import { useHeadings } from './headings-context.js';
import VideoMockup from '../components/VideoMockup.js';
import Lightbox from '../components/Lightbox.js';
import {
  mono,
  formatDate,
  TextLink,
  SectionLabel,
  PostList,
  Contact
} from '../sections/site-chrome.js';
import theme from '../theme';

const t = theme;

// Helper to extract text from React children (handles nested elements)
const getTextFromChildren = (children) => {
  let text = '';
  Children.forEach(children, (child) => {
    if (typeof child === 'string') {
      text += child;
    } else if (typeof child === 'number') {
      text += child.toString();
    } else if (child?.props?.children) {
      text += getTextFromChildren(child.props.children);
    }
  });
  return text;
};

// Helper to create slug from text
const slugify = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-');
};

// Custom heading components that add IDs for anchor links
const headingStyles = {
  h1: {
    fontSize: { xs: '1.75rem', md: '2.25rem' },
    fontWeight: t.typography.fontWeight.medium,
    lineHeight: t.typography.lineHeight.tight,
    letterSpacing: t.typography.letterSpacing.tight,
    mt: 0,
    mb: 4
  },
  h2: {
    fontSize: { xs: '1.35rem', md: '1.5rem' },
    fontWeight: t.typography.fontWeight.medium,
    lineHeight: 1.3,
    letterSpacing: t.typography.letterSpacing.tight,
    mt: 7,
    mb: 2
  },
  h3: {
    fontSize: '1.15rem',
    fontWeight: t.typography.fontWeight.semibold,
    lineHeight: 1.4,
    mt: 5,
    mb: 1.5
  }
};

const createHeading = (level) => {
  // eslint-disable-next-line react/display-name
  return ({ children, ...props }) => {
    const text = getTextFromChildren(children);
    const id =
      slugify(text) || `heading-${Math.random().toString(36).substr(2, 9)}`;
    return (
      <Box
        id={id}
        component={level}
        sx={{
          ...headingStyles[level],
          color: t.colors.textPrimary,
          scrollMarginTop: '96px'
        }}
        {...props}
      >
        {children}
      </Box>
    );
  };
};

const bodyText = {
  color: t.colors.textLight,
  fontSize: '1.0625rem',
  lineHeight: 1.75
};

// Custom components for MDX rendering
const mdxComponents = {
  h1: createHeading('h1'),
  h2: createHeading('h2'),
  h3: createHeading('h3'),
  p: (props) => (
    <Typography component="p" sx={{ ...bodyText, mb: 2.5 }} {...props} />
  ),
  ul: (props) => (
    <Box component="ul" sx={{ pl: 3, mb: 2.5, color: t.colors.textLight }} {...props} />
  ),
  ol: (props) => (
    <Box component="ol" sx={{ pl: 3, mb: 2.5, color: t.colors.textLight }} {...props} />
  ),
  li: (props) => (
    <Typography component="li" sx={{ ...bodyText, mb: 1 }} {...props} />
  ),
  blockquote: (props) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: `2px solid ${t.colors.textPrimary}`,
        mx: 0,
        pl: 3,
        my: 4,
        '& p': { color: t.colors.textPrimary, fontSize: '1.15rem' }
      }}
      {...props}
    />
  ),
  img: (props) => (
    <Lightbox src={props.src} alt={props.alt} type="image">
      <Box
        component="img"
        sx={{
          maxWidth: '100%',
          height: 'auto',
          borderRadius: t.borderRadius.lg,
          border: `1px solid ${t.colors.border}`,
          my: 4,
          display: 'block',
          cursor: 'zoom-in'
        }}
        {...props}
      />
    </Lightbox>
  ),
  pre: (props) => (
    <Box
      component="pre"
      sx={{
        backgroundColor: '#16181d',
        color: '#e4e7ec',
        p: 3,
        borderRadius: t.borderRadius.lg,
        overflow: 'auto',
        my: 4,
        fontFamily: mono,
        fontSize: t.typography.fontSize.sm,
        lineHeight: 1.7,
        // Code blocks reuse the inline <code> component; reset its chip styling
        '& code': {
          backgroundColor: 'transparent',
          border: 'none',
          p: 0,
          color: 'inherit',
          fontSize: 'inherit'
        }
      }}
      {...props}
    />
  ),
  code: (props) => (
    <Box
      component="code"
      sx={{
        backgroundColor: t.colors.backgroundCard,
        border: `1px solid ${t.colors.border}`,
        color: t.colors.textPrimary,
        px: 0.75,
        py: 0.25,
        borderRadius: t.borderRadius.sm,
        fontFamily: mono,
        fontSize: '0.85em'
      }}
      {...props}
    />
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{ border: 'none', borderTop: `1px solid ${t.colors.border}`, my: 6 }}
    />
  ),
  a: (props) => (
    <Box
      component="a"
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: t.colors.textPrimary,
        textDecoration: 'underline',
        textDecorationColor: t.colors.textMuted,
        textUnderlineOffset: '3px',
        transition: t.transitions.fast,
        '&:hover': { textDecorationColor: t.colors.textPrimary }
      }}
      {...props}
    />
  ),
  strong: (props) => (
    <Box
      component="strong"
      sx={{ fontWeight: t.typography.fontWeight.semibold, color: t.colors.textPrimary }}
      {...props}
    />
  ),
  em: (props) => <Box component="em" sx={{ fontStyle: 'italic' }} {...props} />,
  VideoMockup
};

const WORDS_PER_MINUTE = 220;
const MORE_POSTS_COUNT = 3;

function ArticleContent() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const contentRef = useRef(null);
  const { setHeadings, setActiveId } = useHeadings();
  const [minutes, setMinutes] = useState(null);

  // Extract headings after content renders
  useEffect(() => {
    if (contentRef.current) {
      const elements = contentRef.current.querySelectorAll('h1, h2, h3');
      const items = Array.from(elements).map((el) => ({
        id: el.id,
        text: el.textContent,
        level: parseInt(el.tagName[1])
      }));
      setHeadings(items);

      const words = contentRef.current.textContent.trim().split(/\s+/).length;
      setMinutes(Math.max(1, Math.round(words / WORDS_PER_MINUTE)));
    }

    // Clear headings when unmounting
    return () => setHeadings([]);
  }, [slug, setHeadings]);

  // Track active heading on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -80% 0px' }
    );

    if (contentRef.current) {
      const elements = contentRef.current.querySelectorAll('h1, h2, h3');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, [slug, setActiveId]);

  if (!post) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Post not found
        </Typography>
        <Link to="/writings" style={{ color: t.colors.textPrimary }}>
          ← Back to writings
        </Link>
      </Box>
    );
  }

  const { Content } = post;
  const morePosts = getAllPosts()
    .filter((other) => other.slug !== post.slug)
    .slice(0, MORE_POSTS_COUNT);

  const meta = [
    formatDate(post.date),
    post.type === 'case-study' ? 'Case study' : 'Post',
    minutes && `${minutes} min read`
  ].filter(Boolean);

  return (
    <Box component="article" sx={{ maxWidth: 680, mx: 'auto', pb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          mb: 4
        }}
      >
        <TextLink to="/writings/">← All writing</TextLink>
        <Box
          sx={{
            fontFamily: mono,
            fontSize: t.typography.fontSize.xs,
            color: t.colors.textMuted,
            alignSelf: 'center'
          }}
        >
          {meta.join(' · ')}
        </Box>
      </Box>

      {post.cover && (
        <Box
          component="img"
          src={post.cover}
          alt=""
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: t.borderRadius.lg,
            border: `1px solid ${t.colors.border}`,
            mb: 5
          }}
        />
      )}

      <Box ref={contentRef}>
        <Content components={mdxComponents} />
      </Box>

      {morePosts.length > 0 && (
        <Box sx={{ mt: 10 }}>
          <SectionLabel>Keep reading</SectionLabel>
          <PostList posts={morePosts} />
        </Box>
      )}

      <Box sx={{ mt: 8 }}>
        <SectionLabel>Contact</SectionLabel>
        <Contact title="Enjoyed this? Let's talk." />
      </Box>
    </Box>
  );
}

export default ArticleContent;
